import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import useChangePassword from "../../../api/useChangePassword";
import { useTranslation } from "react-i18next";

const ProfileChangePassword = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { changePassword, loading, error, success, resetState } = useChangePassword(user?.token);
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setValidationError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError("New passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setValidationError("Password must be at least 8 characters long");
      return;
    }

    const result = await changePassword(user?.id, currentPassword, newPassword);
    if (result) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const handleReset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setValidationError("");
    resetState();
  };

  return (
    <Card sx={{ maxWidth: 500, mx: "auto", mt: 3 }}>
      <CardHeader 
        title={t("user.changePassword", "Change Password")}
        subheader="Update your account password"
      />
      <CardContent>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password changed successfully!
            </Alert>
          )}
          
          {(error || validationError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || validationError}
            </Alert>
          )}

          <TextField
            margin="normal"
            fullWidth
            label="Current Password"
            type="password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          
          <TextField
            margin="normal"
            fullWidth
            label="New Password"
            type="password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Password must be at least 8 characters long"
          />
          
          <TextField
            margin="normal"
            fullWidth
            label="Confirm New Password"
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Change Password"
              )}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfileChangePassword;